import {
  documentMetaSchema,
  type DocumentVersion,
} from "@prismicio/editor-fields"
import { request } from "@prismicio/editor-support/Http"
import { preload } from "@prismicio/editor-support/Suspense"
import {
  collectWidgets,
  Document,
  DocumentLegacy,
  IntegrationFieldContent,
} from "@prismicio/types-internal/lib/content"
import { string, z } from "zod"

import { env } from "../env"
// import { getRepositoryName } from "../routing"

export const baseUrl = getCoreApiBaseUrl()

//--------------------------------------
// Get Document Version
//--------------------------------------

const versionOkType = z.record(z.unknown())

export function getDocumentByVersion(versionId: string) {
  const url = new URL(`documents/data/${versionId}`, baseUrl)
  return request(url, { okType: versionOkType })
}

export interface CreateDocumentParams {
  customTypeId: string
  content: Document
  title?: string
  tags?: string[]
  groupLangId?: string
  releaseId?: string
  locale: string
}

export function createDocument(params: CreateDocumentParams) {
  const { customTypeId, content, title, tags, groupLangId, releaseId, locale } =
    params

  const defaultTitle = `Untitled ${new Date().toISOString()}`
  const url = new URL("documents", baseUrl)
  const body = {
    title: title ? title : defaultTitle,
    integration_field_ids: getIntegrationFieldIds(content),
    tags: tags ?? [],
    custom_type_id: customTypeId,
    group_lang_id: groupLangId,
    locale,
    release_id: releaseId,
    data: DocumentLegacy.encode(content),
  }

  return request(url, {
    method: "POST",
    body,
    okType: documentMetaSchema,
  })
}

export interface SaveDocumentParams {
  id: string
  content: Document
  documentTags: Set<string>
  status: DocumentVersionStatus
  baseVersionId?: string
  releaseId?: string
}

export function saveDocument(params: SaveDocumentParams) {
  const { id, baseVersionId, documentTags, content, status, releaseId } = params

  const url = new URL(`documents/${id}/${saveDocumentStatus[status]}`, baseUrl)
  if (baseVersionId) url.searchParams.set("base_version_id", baseVersionId)
  if (releaseId) url.searchParams.set("release_id", releaseId)

  const body = {
    integration_field_ids: getIntegrationFieldIds(content),
    tags: Array.from(documentTags),
    data: DocumentLegacy.encode(content),
  }

  return request(url, {
    method: "PUT",
    body,
    okType: documentMetaSchema,
  })
}

// When we interact with unclassified, published or archived docs we create a new unsaved draft
// When we save the unsaved draft we create a new unclassified version
// Releases behave differently editing them does not create a new unsaved draft
// and Saving the changes saves to the current release version.
const saveDocumentStatus = {
  unclassified: "draft",
  published: "draft",
  archived: "draft",
  release: "release",
}

export interface SaveTitleParams {
  id: string
  title: string
}

export function saveTitle(params: SaveTitleParams) {
  const { id, title } = params

  const url = new URL(`documents/${id}`, baseUrl)

  const body = { title }

  return request(url, { method: "PATCH", body, okType: z.unknown() })
}

export function publishDocument(metaId: string) {
  const url = new URL(`documents/${metaId}/draft`, baseUrl)
  const body = { status: "published" }

  return request(url, {
    method: "PATCH",
    body,
    okType: z.unknown(),
  })
}

export function unpublishDocument(
  metaId: string,
  status: "published" | "release",
) {
  return archiveDocument(metaId, status)
}

export function archiveDocument(
  metaId: string,
  status: "unclassified" | "published" | "release",
  releaseId?: string,
) {
  const url = new URL(`documents/${metaId}/${statusToAPIPath[status]}`, baseUrl)
  const body = { status: "archived", release_id: releaseId }

  return request(url, {
    method: "PATCH",
    body,
    okType: z.unknown(),
  })
}

const statusToAPIPath = {
  unclassified: "draft",
  published: "live",
  release: "release",
}

export type DocumentVersionStatus = DocumentVersion["status"]

export function getPreviewUrls() {
  const url = new URL("repository/preview_configs", baseUrl)
  return request(url, { okType: preViewUrlsResponse })
}

export function preloadPreviewUrls() {
  preload(getPreviewUrls, [])
}

const previewUrl = z.object({
  id: z.string(),
  label: z.string(),
  url: z.string().url(),
})

const preViewUrlsResponse = z.object({
  results: z.array(previewUrl),
})

export type PreviewUrl = z.infer<typeof previewUrl>

const redirectUrlResponse = z.object({
  preview_url: string().url(),
  session_id: string(),
})

export type RedirectURL = z.infer<typeof redirectUrlResponse>

export function refreshRelease(releaseId: string) {
  const url = new URL(`releases/${releaseId}/refresh`, baseUrl)

  return request(url, { method: "POST", okType: z.any() })
}

export function getRedirectURL(
  previewId: string,
  documentId: string,
  version: DocumentVersion,
) {
  switch (version.status) {
    case "release": {
      const url = new URL(
        `previews/session/releases/${version.release_id}`,
        baseUrl,
      )

      url.searchParams.set("previewId", previewId)
      url.searchParams.set("documentId", documentId)
      url.searchParams.set("versionId", version.version_id)

      return request(url, { okType: redirectUrlResponse })
    }
    case "published": {
      const url = new URL("previews/session/live", baseUrl)

      url.searchParams.set("previewId", previewId)
      url.searchParams.set("documentId", documentId)

      return request(url, { okType: redirectUrlResponse })
    }
    // Any other version is treated as a draft
    default: {
      const url = new URL("previews/session/draft", baseUrl)

      url.searchParams.set("previewId", previewId)
      url.searchParams.set("documentId", documentId)
      url.searchParams.set("versionId", version.version_id)

      return request(url, { okType: redirectUrlResponse })
    }
  }
}

function getIntegrationFieldIds(document: Document) {
  const integrationFieldContent = collectWidgets(
    document,
    (content): content is IntegrationFieldContent =>
      content.__TYPE__ === "IntegrationFieldsContent" && Boolean(content.value),
  )
  return Object.values(integrationFieldContent).map((content) => content.value)
}

function getCoreApiBaseUrl(): URL {
  const coreApiBaseUrl = new URL("core/", env.PRISMIC_WROOM_BASE_URL)
  // coreApiBaseUrl.hostname = `${getRepositoryName()}.${coreApiBaseUrl.hostname}`
  return coreApiBaseUrl
}
